import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import {
  CLIENT_STATUS_FILTER_OPTIONS,
  TERMINAL_STATUSES,
  STATUS_LABEL,
  STATUS_TONE,
  groupStatusCounts,
  groupClientStatusCounts,
  PRIORITY_LABEL,
  STATUS_FILTER_OPTIONS,
  PRIORITIES,
  hasPermission,
  isClientStatusRole,
  getClientDisplayStatus,
  getTicketDisplayStatus,
} from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate } from "@/lib/date";
import StatusBadge from "@/components/common/StatusBadge";
import PriorityBadge from "@/components/common/PriorityBadge";
import HBarChart from "@/components/charts/HBarChart";
import {
  BarChart3,
  CheckCircle2,
  Clock3,
  Download,
  FileEdit,
  Layers,
  RefreshCcw,
  Search,
  ShieldAlert,
} from "lucide-react";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

const GOLD = "#2563eb";

const STATUS_TONE_COLORS = {
  blue: "#3b82f6", cyan: "#0891b2", gold: "#8b5cf6", amber: "#f59e0b", orange: "#f97316",
  green: "#16a34a", red: "#ef4444", slate: "#64748b", purple: "#8b5cf6",
};

function getStatusColor(status, index = 0) {
  const fallback = [
    "#2563eb",
    "#3b82f6",
    "#8b5cf6",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#06b6d4",
    "#ec4899",
  ];

  return STATUS_TONE_COLORS[STATUS_TONE[status]] || fallback[index % fallback.length];
}

const PRIORITY_COLORS = {
  P1: "#dc2626",
  P2: "#f97316",
  P3: "#f59e0b",
  P4: "#2563eb",
  P5: "#94a3b8",
};

const EMPTY_SUMMARY = {
  total: 0,
  open: 0,
  closed: 0,
  missed_sla: 0,
  sla_at_risk: 0,
  avg_resolution_hours: 0,
  by_status: [],
  by_priority: [],
  by_organization: [],
  aging: {
    "0_1_days": 0,
    "2_3_days": 0,
    "4_7_days": 0,
    "8_15_days": 0,
    "15_plus_days": 0,
  },
};

export default function ReportsPage() {
  const { user } = useAuth();
  const isClientReportView = isClientStatusRole(user?.role);
  const completedStatusFilter = isClientReportView
    ? [...TERMINAL_STATUSES].sort().join(",")
    : "completed";
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const loadReports = useCallback(async (showRefresh = false) => {
    try {
      setError("");
      if (showRefresh) setRefreshing(true);
      else setLoading(true);

      const params = {};
      if (status) params.status = status;
      if (priority) params.priority = priority;

      const [ticketsResponse, summaryResponse] = await Promise.all([
        api.get("/reports/tickets", { params }),
        api.get("/reports/summary"),
      ]);

      setRows(
        Array.isArray(ticketsResponse.data) ? ticketsResponse.data : []
      );
      setSummary({
        ...EMPTY_SUMMARY,
        ...(summaryResponse.data || {}),
        aging: {
          ...EMPTY_SUMMARY.aging,
          ...(summaryResponse.data?.aging || {}),
        },
      });
    } catch (err) {
      console.error("Unable to load reports:", err);
      setError(
        err?.response?.data?.detail ||
          "Unable to load report analytics. Please try again."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [status, priority]);

  useEffect(() => {
    loadReports();
    window.addEventListener("focus", loadReports);
    window.addEventListener("helpdesk-ticket-changed", loadReports);
    return () => {
      window.removeEventListener("focus", loadReports);
      window.removeEventListener("helpdesk-ticket-changed", loadReports);
    };
  }, [loadReports]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;

    return rows.filter((row) =>
      [
        row.ticket_number,
        row.title,
        row.application_code,
        row.organization_name,
        STATUS_LABEL[isClientReportView ? getClientDisplayStatus(row) : getTicketDisplayStatus(row)] || getTicketDisplayStatus(row) || "—",
        PRIORITY_LABEL[row.priority] || row.priority,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [rows, search, isClientReportView]);

  const navigate = useNavigate();

  const statusData = useMemo(
    () =>
      (isClientReportView
        ? groupClientStatusCounts(summary.by_status)
        : groupStatusCounts(summary.by_status)).map((bar, index) => ({
        ...bar,
        color: getStatusColor(bar.colorStatus || bar.statuses[0], index),
      })),
    [summary.by_status, isClientReportView]
  );

  const priorityData = useMemo(
    () =>
      (summary.by_priority || []).map((item) => ({
        name: PRIORITY_LABEL[item._id] || item._id || "Unknown",
        code: item._id,
        value: item.count || 0,
        color: PRIORITY_COLORS[item._id] || "#94a3b8",
      })).sort((a, b) => String(a.code).localeCompare(String(b.code))),
    [summary.by_priority]
  );

  const organizationData = useMemo(
    () =>
      (summary.by_organization || []).map((item) => ({
        name:
          item.name ||
          item.organization_name ||
          item._id ||
          "Unassigned organization",
        id: item.organization_id,
        value: item.total ?? item.count ?? 0,
      })).sort((a, b) => b.value - a.value),
    [summary.by_organization]
  );

  const agingData = useMemo(
    () => [
      {
        name: "0–1 days",
        key: "0_1_days",
        value: summary.aging?.["0_1_days"] || 0,
      },
      {
        name: "2–3 days",
        key: "2_3_days",
        value: summary.aging?.["2_3_days"] || 0,
      },
      {
        name: "4–7 days",
        key: "4_7_days",
        value: summary.aging?.["4_7_days"] || 0,
      },
      {
        name: "8–15 days",
        key: "8_15_days",
        value: summary.aging?.["8_15_days"] || 0,
      },
      {
        name: "15+ days",
        key: "15_plus_days",
        value: summary.aging?.["15_plus_days"] || 0,
      },
    ],
    [summary.aging]
  );

  const csvParams = new URLSearchParams();
  if (status) csvParams.set("status", status);
  if (priority) csvParams.set("priority", priority);

  const csvUrl = `${API_BASE}/reports/tickets.csv${
    csvParams.toString() ? `?${csvParams.toString()}` : ""
  }`;

  const clearFilters = () => {
    setStatus("");
    setPriority("");
    setSearch("");
  };

  const kpis = [
    {
      label: "Total tickets",
      value: summary.total,
      icon: Layers,
      tone: "text-foreground",
      href: "/tickets",
    },
    {
      label: isClientReportView ? "Active tickets" : "Open tickets",
      value: summary.open,
      icon: FileEdit,
      tone: "text-sky-700",
      href: "/tickets?open=true",
    },
    {
      label: "Completed tickets",
      value: summary.closed,
      icon: CheckCircle2,
      tone: "text-emerald-700",
      href: `/tickets?status=${completedStatusFilter}`,
    },
    {
      // Open tickets already past their resolution due time.
      label: "SLA at risk",
      value: summary.missed_sla,
      icon: ShieldAlert,
      tone: "text-rose-700",
      href: "/tickets?sla=at_risk",
    },
    {
      // Calculated from closed tickets.
      label: "Avg. resolution",
      value: formatHours(summary.avg_resolution_hours),
      icon: Clock3,
      tone: "text-violet-700",
      compact: true,
      href: "/tickets?status=completed",
    },
  ];

  if (loading) {
    return <ReportsSkeleton />;
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Analytics · service intelligence
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight">
            Reports <span className="gold-text">& Analytics</span>
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Monitor ticket volume, SLA exposure, resolution performance and
            operational workload.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => loadReports(true)}
            disabled={refreshing}
          >
            <RefreshCcw
              className={`h-4 w-4 mr-2 ${
                refreshing ? "animate-spin" : ""
              }`}
            />
            Refresh
          </Button>

          {hasPermission(user, "export_reports") && (
            <a
              href={csvUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="download-csv"
            >
              <Button>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </a>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3">
        {kpis.map((kpi) => {
          const Icon = kpi.icon;

          return (
            <Link
              key={kpi.label}
              to={kpi.href}
              data-testid={`report-kpi-${kpi.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="block rounded-xl border border-border bg-card p-4 hover:border-primary/35 transition-colors"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  {kpi.label}
                </div>
                <Icon className={`h-4 w-4 ${kpi.tone}`} />
              </div>

              <div
                className={`mt-2 font-display font-bold ${kpi.tone} ${
                  kpi.compact ? "text-2xl" : "text-3xl"
                }`}
              >
                {kpi.value}
              </div>
            </Link>
          );
        })}
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Report filters
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              Filter the ticket register by status, priority or keyword.
            </div>
          </div>

          {(status || priority || search) && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <Select
            value={status || "__all__"}
            onValueChange={(value) =>
              setStatus(value === "__all__" ? "" : value)
            }
          >
            <SelectTrigger data-testid="report-status-filter">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="__all__">All Status</SelectItem>
              {(isClientReportView ? CLIENT_STATUS_FILTER_OPTIONS : STATUS_FILTER_OPTIONS).map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={priority || "__all__"}
            onValueChange={(value) =>
              setPriority(value === "__all__" ? "" : value)
            }
          >
            <SelectTrigger data-testid="report-priority-filter">
              <SelectValue placeholder="All priorities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All priorities</SelectItem>
              {PRIORITIES.map((item) => (
                <SelectItem key={item} value={item}>
                  {PRIORITY_LABEL[item] || item}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search ticket number, title or application…"
              className="pl-9"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <ChartCard
          title="Tickets by status"
          subtitle="Current ticket distribution across workflow stages"
          className="xl:col-span-2"
        >
          {statusData.length ? (
            <HBarChart
              data={statusData}
              yLabel="Status"
              tooltip={<ReportTooltip />}
              onBarClick={(d) => d?.status && navigate(`/tickets?status=${d.status}`)}
            />
          ) : (
            <EmptyChart />
          )}
        </ChartCard>

        <ChartCard
          title="Priority distribution"
          subtitle="Ticket workload grouped by urgency"
        >
          {priorityData.length ? (
            <>
              <ResponsiveContainer width="100%" height={245}>
                <PieChart>
                  <Pie
                    data={priorityData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={58}
                    outerRadius={88}
                    paddingAngle={3}
                    stroke="#ffffff"
                    strokeWidth={2}
                    cursor="pointer"
                    onClick={(d, index) => {
                      const code = priorityData[index]?.code ?? d?.payload?.code;
                      if (code) navigate(`/tickets?priority=${code}`);
                    }}
                  >
                    {priorityData.map((item, index) => (
                      <Cell
                        key={item.code || item.name}
                        fill={item.color}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<ReportTooltip />} />
                </PieChart>
              </ResponsiveContainer>

              <div className="space-y-2">
                {priorityData.map((item, index) => (
                  <button
                    type="button"
                    key={item.code || item.name}
                    onClick={() => item.code && navigate(`/tickets?priority=${item.code}`)}
                    className="flex w-full items-center justify-between rounded-md px-1.5 py-1 text-xs hover:bg-muted/60"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="h-2.5 w-2.5 rounded-full shrink-0"
                        style={{
                          backgroundColor: item.color,
                        }}
                      />
                      <span className="text-muted-foreground truncate">
                        {item.name}
                      </span>
                    </div>
                    <span className="font-mono font-semibold">
                      {item.value}
                    </span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <EmptyChart />
          )}
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartCard
          title="Organization workload"
          subtitle="Ticket volume across accessible organizations"
        >
          {organizationData.length ? (
            <HBarChart
              data={organizationData}
              yLabel="Organization"
              color={GOLD}
              tooltip={<ReportTooltip />}
              onBarClick={(d) =>
                d?.id &&
                navigate(`/tickets?organization_id=${encodeURIComponent(d.id)}&org=${encodeURIComponent(d.name)}`)
              }
            />
          ) : (
            <EmptyChart />
          )}
        </ChartCard>

        <ChartCard
          title="Open-ticket aging"
          subtitle="Age of tickets that remain unresolved"
        >
          <HBarChart
            data={agingData}
            yLabel="Ticket age"
            xLabel="Number of open tickets"
            color="#8b5cf6"
            tooltip={<ReportTooltip />}
            onBarClick={(d) => d?.key && navigate(`/tickets?open=true&age=${d.key}`)}
          />
        </ChartCard>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="flex items-center justify-between gap-4 flex-wrap px-4 py-4 border-b border-border">
          <div>
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" />
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Ticket register
              </div>
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {filteredRows.length} visible{" "}
              {filteredRows.length === 1 ? "ticket" : "tickets"}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-secondary/20">
              <tr className="border-b border-border">
                <th className="text-left px-4 py-3">Number</th>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Priority</th>
                <th className="text-left px-4 py-3">Application</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-left px-4 py-3">Completed</th>
              </tr>
            </thead>

            <tbody>
              {filteredRows.map((row) => (
                <tr
                  key={row.id}
                  data-row
                  className="border-b border-border/70 hover:bg-muted/60 transition-colors"
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/tickets/${row.id}`}
                      className="font-mono text-xs text-primary hover:underline"
                    >
                      {row.ticket_number}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/tickets/${row.id}`}
                      className="block max-w-md truncate hover:text-primary transition-colors"
                    >
                      {row.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={isClientStatusRole(user?.role)
                      ? getClientDisplayStatus(row)
                      : getTicketDisplayStatus(row)} />
                  </td>
                  <td className="px-4 py-3">
                    <PriorityBadge priority={row.priority} />
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {row.application_code || "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {formatDate(row.created_at)}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {row.closed_at ? formatDate(row.closed_at) : "—"}
                  </td>
                </tr>
              ))}

              {!filteredRows.length && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-14 text-center text-sm text-muted-foreground"
                  >
                    No tickets match the selected report filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ReportsSkeleton() {
  return (
    <div
      className="space-y-6"
      data-testid="reports-skeleton"
    >
      <div className="space-y-3">
        <Skeleton className="h-3 w-44" />
        <Skeleton className="h-10 w-full max-w-xl" />
        <Skeleton className="h-4 w-full max-w-2xl" />
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={index}
            className="rounded-xl border border-border bg-card p-4"
          >
            <div className="flex items-center justify-between">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-4 w-4 rounded-full" />
            </div>

            <Skeleton className="mt-4 h-9 w-20" />
          </div>
        ))}
      </div>

      <Skeleton className="h-28 w-full rounded-xl" />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-4 xl:col-span-2">
          <Skeleton className="mb-4 h-3 w-32" />
          <Skeleton className="h-[220px] w-full" />
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <Skeleton className="mb-4 h-3 w-32" />
          <Skeleton className="mx-auto h-[180px] w-[180px] rounded-full" />
        </div>
      </div>

      <Skeleton className="h-[300px] w-full rounded-xl" />
    </div>
  );
}


function ChartCard({ title, subtitle, children, className = "" }) {
  return (
    <div
      className={`rounded-xl border border-border bg-card p-4 ${className}`}
    >
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          {title}
        </div>
        {subtitle && (
          <div className="text-xs text-muted-foreground mt-1">
            {subtitle}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="h-[260px] flex flex-col items-center justify-center text-center">
      <BarChart3 className="h-8 w-8 text-muted-foreground/50 mb-3" />
      <div className="text-sm text-muted-foreground">
        No report data available
      </div>
      <div className="text-xs text-muted-foreground/70 mt-1">
        Analytics will appear when accessible tickets are available.
      </div>
    </div>
  );
}

function ReportTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const item = payload[0];
  const source = item?.payload || {};

  return (
    <div className="md-chart-tooltip min-w-[150px] rounded-lg border px-3 py-2 shadow-xl">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label || source.name || "Details"}
      </div>

      <div className="mt-1 flex items-center justify-between gap-5">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full"
            style={{
              backgroundColor:
                source.color ||
                item.color ||
                GOLD,
            }}
          />

          <span className="truncate text-sm font-medium">
            {source.name || item.name || "Tickets"}
          </span>
        </div>

        <span className="font-mono text-sm font-bold">
          {item.value ?? source.value ?? 0}
        </span>
      </div>
    </div>
  );
}

function formatHours(value) {
  const hours = Number(value || 0);

  if (!Number.isFinite(hours) || hours <= 0) return "0h";
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${hours.toFixed(1)}h`;

  const days = Math.floor(hours / 24);
  const remainingHours = Math.round(hours % 24);

  return remainingHours ? `${days}d ${remainingHours}h` : `${days}d`;
}
